import Vuex from 'vuex'
import { uploadFileToSubmission, submissionFileTypeToNumber, ensureAuthorsSanitised } from './functions'
import authModule from './auth/module'
import Guid from '../utils/Guid'
import VuexPersistence from 'vuex-persist'
import {
  State,
  ArticleType,
  Submission,
  EditorialOfficeSelectionMode,
  ReviewerAnonymisationType,
  TransparentPeerReviewType,
  SubmissionFileState,
  SubmissionFile,
  SubmissionFileType,
  TitleAbstract,
  Author,
  NewSubmissionParams,
  Reviewer,
  CompareFileIdParams,
  EditorialOffice,
  VerifyResult,
  UploadDasParams,
  LocalStorage,
  DasOption,
  FeatureFlagsPayload,
  ValidationKey,
} from '@/types'
import submissionApi from '../api/submission-api'
import validateReviewStep from '../services/validation/finalReview'
import validateReviewEditorialView from '../services/validation/reviewEditorial'
import validateAuthorInfoView from '../services/validation/authorInfo'
import validateUploadFilesView from '../services/validation/uploadFiles'
import { clearLocalStorageExpiration, createLocalStorageExpiration } from '../services/localStorageManager'
import { ActionContext } from 'vuex/types/index.js'
import { toRaw } from 'vue'

const buildNewSubmission = function (id: string, articleType?: ArticleType): Submission {
  return {
    title: '',
    abstract: '',
    articleType: articleType,
    files: [],
    authors: [],
    editorialOfficeIds: [],
    reviewers: [],
    termsAgreed: false,
    submissionId: id,
    ccdcNumbers: '',
    peerReviewAnonymisation: ReviewerAnonymisationType.Unknown,
    transparentPeerReview: TransparentPeerReviewType.Unknown,
    userIdentifier: '',
    themedCollectionTitle: '',
    themedCollectionResponse: '',
  }
}

export const initialState = {
  activeSubmission: buildNewSubmission(Guid.generate(), undefined) as Submission,
  userIdentifier: '',
  completed: false,
  submissionFiles: {
    CoverLetter: [],
    MainArticle: [],
    DAS: [],
    CrystalData: [],
    Esi: [],
    Figure: [],
    Unknown: [],
  },
  errors: {
    uploadFilesVerifyResult: { valid: false, errors: [], failedRules: {}, field: 'Files', rules: '' },
    articleInfoVerifyResult: { valid: false, errors: [], failedRules: {}, field: 'ArticleInfo', rules: '' },
    authorInfoVerifyResult: { valid: false, errors: [], failedRules: {}, field: 'Authors', rules: '' },
    reviewEditorialVerifyResult: { valid: false, errors: [], failedRules: {}, field: 'ReviewerInfo', rules: '' },
    finalReviewVerifyResult: { valid: false, errors: [], failedRules: {}, field: 'FinalReview', rules: '' },
    dasFileVerifyResult: { valid: false, errors: [], failedRules: {}, field: 'DAS', rules: '' },
  } as Record<string, VerifyResult>,
  modalShownSinceExtraction: true,
  hasExtractedDataFromFile: false,
  extractedSnapshot: {},
  finalReviewVisited: false,
  currentlySelectedEditorialOffices: [] as string[],
  editorialOfficeSelectionMode: EditorialOfficeSelectionMode.AuthorDecided,
  step: 0,
  das: {
    dasCustomSelected: false,
    dasCustomContent: '',
    dasCustomIsSent: false,
    dasFileIsUploaded: false,
    dasReview: false,
    dasReviewIsSent: false,
    selectedOption: '' as '' | DasOption,
  },
  submissionAttemptCounter: 0,
  themedCollectionTitle: '',
  themedCollectionResponse: '',
  showExtractionModal: false,
  dataToCompare: {
    title: '',
    abstract: '',
    authors: [],
    ingestedAt: null,
  },
  featureFlagsEnabled: [],
  editorialOffices: [] as EditorialOffice[],
}

let expirationDate: string

export default new Vuex.Store<State>({
  modules: {
    auth: authModule,
  },
  state: structuredClone(initialState),
  mutations: {
    _setStep(state: State, step: number) {
      state.step = step
    },
    _startNewSubmission(state: State, { id, articleType }: NewSubmissionParams) {
      state.activeSubmission = buildNewSubmission(id, articleType)
      state.finalReviewVisited = false
      expirationDate = createLocalStorageExpiration()
    },
    setVerifyResult(
      state: State,
      { key, verifyResult }: { key: ValidationKey, verifyResult: VerifyResult },
    ) {
      verifyResult.field = verifyResult.field || ''
      verifyResult.rules = verifyResult.rules || ''

      state.errors[key] = verifyResult
    },
    setFinalReviewVisited(state: State, value: boolean) {
      bindEvent(window, 'message', function (e: MessageEvent) {
        // to set user userIdentifier from S1.
        if (e.origin == 'https://mc.manuscriptcentral.com') {
          var isValidGuid = Guid.validate(e.data)
          if (isValidGuid) {
            state.activeSubmission.userIdentifier = e.data
          }
        }
      })

      state.finalReviewVisited = value
      ensureAuthorsSanitised(state.activeSubmission)
    },
    clearSubmission(state: State) {
      window.localStorage.clear()
      expirationDate = clearLocalStorageExpiration()
      const applicationState = {
        featureFlagsEnabled: state.featureFlagsEnabled,
      }
      Object.assign(state, initialState, applicationState)
    },
    _clearAllAuthors(state: State) {
      state.activeSubmission.authors = []
    },
    _addFile(state: State, submissionFile: SubmissionFile) {
      const copyOfActiveSubmissionFiles = [...state.activeSubmission.files]
      copyOfActiveSubmissionFiles.push(submissionFile)
      state.activeSubmission.files = copyOfActiveSubmissionFiles
    },
    _fileUploadFailure(state: State, id: string) {
      var file = state.activeSubmission.files.find(file => file.id === id) as SubmissionFile
      if (file) {
        file.state = SubmissionFileState.Failure
      }
    },
    _fileUploadDuplicate(state: State, id: string) {
      var file = state.activeSubmission.files.find(file => file.id === id) as SubmissionFile
      if (file) {
        file.state = SubmissionFileState.FailureDuplicate
      }
    },
    _fileUploadSuccess(state: State, { oldId, newId }: CompareFileIdParams) {
      var file = state.activeSubmission.files.find(file => file.id === oldId) as SubmissionFile
      if (file) {
        file.id = newId
        file.state = SubmissionFileState.Complete
      }
    },
    _setTitle(state: State, title: string) {
      state.activeSubmission.title = title
    },
    _setAbstract(state: State, abstract: string) {
      state.activeSubmission.abstract = abstract
    },
    _setCcdcNumbers(state: State, ccdcNumbers: string) {
      state.activeSubmission.ccdcNumbers = ccdcNumbers
    },
    _addAuthor(state: State, author: Author) {
      if (state.activeSubmission.authors.filter(innerAuthor => innerAuthor.id === author.id).length)
        return
      state.activeSubmission.authors.push(author)
      ensureAuthorsSanitised(state.activeSubmission)
    },
    _updateAuthor(state: State, author: Author) {
      var index = state.activeSubmission.authors.findIndex(a => a.id === author.id)
      if (index < 0) {
        throw new Error(`Cannot update author, author ${author.id} does not exist`)
      }
      // only one author at a time can be corresponding
      if (author.isCorresponding) {
        state.activeSubmission.authors.forEach((a: { isCorresponding: boolean }) => {
          a.isCorresponding = false
        })
      }
      state.activeSubmission.authors.splice(index, 1, author)
      ensureAuthorsSanitised(state.activeSubmission)
    },
    _deleteAuthor(state: State, id: string) {
      var index = state.activeSubmission.authors.findIndex(author => author.id === id)
      if (index < 0) {
        throw new Error(`Cannot delete author, author ${id} does not exist`)
      }

      var targetAuthor: Author = state.activeSubmission.authors[index]
      state.activeSubmission.authors.splice(index, 1)

      if (targetAuthor.isCorresponding && state.activeSubmission.authors.length > 0) {
        state.activeSubmission.authors[0].isCorresponding = true
      }
    },
    _deleteAuthors(state: State) {
      while (state.activeSubmission.authors.length) {
        state.activeSubmission.authors.splice(0, 1)
      }
    },
    _removeFile(state: State, id: string) {
      state.activeSubmission.files = state.activeSubmission.files.filter(file => file.id !== id) as SubmissionFile[]
      if (!state.activeSubmission.files.some(file => file.type === SubmissionFileType.CrystalData)) {
        state.activeSubmission.ccdcNumbers = ''
      }
    },
    _addEditorialOffice(state: State, editor: EditorialOffice) {
      state.currentlySelectedEditorialOffices.push(editor.peerReviewIdentifier)
      state.activeSubmission.editorialOfficeIds.push(editor.peerReviewIdentifier)
    },
    _setEditorialOfficeSelectionMode(state: State, editorialOfficeSelectionMode: EditorialOfficeSelectionMode) {
      state.editorialOfficeSelectionMode = editorialOfficeSelectionMode
      if (editorialOfficeSelectionMode === EditorialOfficeSelectionMode.RscDecide) {
        state.activeSubmission.editorialOfficeIds = ['Cambridge', 'Cambridge']
      }
      else {
        const rawEditorialOffices = toRaw(state.currentlySelectedEditorialOffices)
        state.activeSubmission.editorialOfficeIds = structuredClone(rawEditorialOffices)
      }
    },
    _setReviewerAnonymisation(state: State, anonymisation: ReviewerAnonymisationType) {
      state.activeSubmission.peerReviewAnonymisation = anonymisation
      // Push to data layer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'peerReviewAnonymisationChange',
          peerReviewAnonymisation: anonymisation,
        })
      }
    },
    _setTPRType(state: State, transparentPeerReview: TransparentPeerReviewType) {
      state.activeSubmission.transparentPeerReview = transparentPeerReview
      // Push to data layer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'transparentPeerReview',
          transparentPeerReview: transparentPeerReview,
        })
      }
    },
    _upsertReviewer(state: State, reviewer: Reviewer) {
      var index = state.activeSubmission.reviewers.findIndex(r => r.id === reviewer.id)
      if (index < 0) {
        state.activeSubmission.reviewers.push(reviewer)
      }

      state.activeSubmission.reviewers.splice(index, 1, reviewer)
    },
    _removeReviewer(state: State, id: string) {
      state.activeSubmission.reviewers = state.activeSubmission.reviewers.filter(reviewer => reviewer.id !== id) as Reviewer[]
    },
    _updateTermsAgreed(state: State, agree: boolean) {
      state.activeSubmission.termsAgreed = agree
    },
    _markAsComplete(state: State) {
      state.completed = true
    },
    setDasFileIsUploaded(state: State, value: boolean) {
      state.das.dasFileIsUploaded = value
    },
    resetDasFileIsUploaded(state: State) {
      state.das.dasFileIsUploaded = false
    },
    setDasCustomContent(state: State, content: string) {
      state.das.dasCustomContent = content
    },
    setDasReview(state: State, value: boolean) {
      state.das.dasReview = value
    },
    setDasCustomSelected(state: State, value: boolean) {
      state.das.dasCustomSelected = value
    },
    setSelectedOption(state: State, option: DasOption | '') {
      state.das.selectedOption = option
      // Push to data layer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'dasSelectedOptionChange',
          dasSelectedOptionChange: option,
        })
      }
    },
    setDasCustomIsSent(state: State, value: boolean) {
      state.das.dasCustomIsSent = value
    },
    setThemedCollectionResponse(state: State, response: string) {
      state.activeSubmission.themedCollectionResponse = response
    },
    setThemedCollectionTitle(state: State, title: string) {
      state.activeSubmission.themedCollectionTitle = title
    },
    incrementSubmissionAttemptCounter(state) {
      state.submissionAttemptCounter++
    },
    removeSubmissionFile(state: State, payload: { fileType: SubmissionFileType, fileName?: string }) {
      const { fileType, fileName } = payload
      if (fileName) {
        state.activeSubmission.files = state.activeSubmission.files.filter(
          file => !(file.type === fileType && file.file === fileName),
        ) as SubmissionFile[]
      }
    },
    setGroupedSubmissionFiles(state, files) {
      state.activeSubmission.files = files
    },
    setReviewers(state, reviewers) {
      state.activeSubmission.reviewers = reviewers
    },
    setAuthors(state, authors) {
      state.activeSubmission.authors = authors
      ensureAuthorsSanitised(state.activeSubmission)
    },
    _setDataToCompare(
      state: State,
      payload: { title: string, abstract: string, authors: Author[], ingestedAt: string },
    ) {
      const { title, abstract, authors, ingestedAt } = payload
      state.dataToCompare = {
        title: title || '',
        abstract: abstract || '',
        authors: authors ? structuredClone(authors) : [],
        ingestedAt: ingestedAt || new Date().toISOString(),
      }
    },
    setModalShownSinceExtraction(state: State, value: boolean) {
      state.modalShownSinceExtraction = value
    },
    _setExtractedModalToShow(state: State) {
      state.modalShownSinceExtraction = false
    },
    _deleteEditorialOffice(state: State, editorId: string) {
      state.currentlySelectedEditorialOffices = state.currentlySelectedEditorialOffices.filter(
        id => id !== editorId,
      )
      state.activeSubmission.editorialOfficeIds = state.activeSubmission.editorialOfficeIds.filter(
        id => id !== editorId,
      )
    },
    _setSelectedEditorialOffices(state: State, editors: EditorialOffice[]) {
      const ids = editors.map(e => e.peerReviewIdentifier)
      state.currentlySelectedEditorialOffices = [...ids]
      state.activeSubmission.editorialOfficeIds = [...ids]
    },
    _setHasExtractedDataFromFile(state, value: boolean) {
      state.hasExtractedDataFromFile = value
    },
    _setExtractedSnapshot(state, snapshot: { title?: boolean, abstract?: boolean, authors?: boolean }) {
      state.extractedSnapshot = snapshot
    },
    _invalidateExtractedSnapshotField(state: State, field: 'title' | 'abstract' | 'authors') {
      if (!state.extractedSnapshot) state.extractedSnapshot = {}
      state.extractedSnapshot[field] = false
    },
    setFeatureFlagsEnabled(state: State, paramsObj: FeatureFlagsPayload): void {
      const newKeys = Object.keys(paramsObj)
      const uniqueKeys = newKeys.filter(key => !state.featureFlagsEnabled.includes(key))
      state.featureFlagsEnabled.push(...uniqueKeys)
    },
    setEditorialOffices(state: State, offices: EditorialOffice[]) {
      state.editorialOffices = offices
    },
  },
  actions: {
    async refreshToken(_: ActionContext<State, State>, submissionId: string) {
      const resp = await submissionApi.continueSubmission(submissionId)
      const authExpDate = new Date()
      authExpDate.setDate(authExpDate.getDate() + 30)
      document.cookie = `auth=${resp.data.token}; expires=${authExpDate}; path=/; Secure; SameSite=None;`
    },

    async startNewSubmission({ commit }: ActionContext<State, State>, articleType: ArticleType) {
      const resp = await submissionApi.startNewSubmission()
      let authExpDate = new Date()
      authExpDate.setDate(authExpDate.getDate() + 30).toString()
      commit('_startNewSubmission', { id: resp.data.submissionId, articleType: articleType })
      document.cookie = 'auth=' + resp.data.token + '; expires=' + authExpDate + ';path=/;' + ' Secure; SameSite=None;'
    },
    async uploadFile({ commit, state }: ActionContext<State, State>, { file, fileType }) {
      const tempId = Guid.generate()

      commit('_addFile', {
        id: tempId,
        file: file.name,
        type: fileType,
        size: file.size,
        state: SubmissionFileState.Uploading,
        fileData: file,
      })

      await uploadFileToSubmission(commit, state, file, fileType, tempId)

      if (fileType === SubmissionFileType.DAS) {
        commit('setSelectedOption', DasOption.File)
      }

      await validateUploadFilesView(this)
    },
    async uploadDas({ commit, state }: ActionContext<State, State>, { submissionId, content, tempId }: UploadDasParams) {
      if (state.das.selectedOption === DasOption.Custom) {
        const resp = await submissionApi.addCustomDas(submissionId, content, SubmissionFileType.DAS)
        commit('_fileUploadSuccess', { oldId: tempId, newId: resp.data.fileId })
        await validateUploadFilesView(this)
      }
      else if (state.das.selectedOption === DasOption.Review) {
        const resp = await submissionApi.addNoDataDas(submissionId, SubmissionFileType.DAS)
        commit('_fileUploadSuccess', { oldId: tempId, newId: resp.data.fileId })
        await validateUploadFilesView(this)
      }
    },
    async changeDASOption({ commit, state, dispatch }: ActionContext<State, State>, option: DasOption | '') {
      commit('setSelectedOption', option)
      const dasFile = state.activeSubmission.files.find((file: SubmissionFile) => file.type === SubmissionFileType.DAS) as SubmissionFile | undefined
      if (dasFile) {
        commit('resetDasFileIsUploaded')
        commit('setDasCustomIsSent', false)
        await dispatch('deleteFile', dasFile.id)
      }
    },
    async deleteFile({ commit, state }: ActionContext<State, State>, fileId: string) {
      var file = state.activeSubmission.files.find(file => file.id === fileId) as SubmissionFile
      const promise = file.state === SubmissionFileState.Complete
        ? submissionApi.deleteFile(state.activeSubmission.submissionId, fileId)
        : Promise.resolve()

      return promise.then(async () => {
        if (file.type === SubmissionFileType.DAS) {
          commit('resetDasFileIsUploaded')
          commit('setDasCustomIsSent', false)
        }
        commit('_removeFile', fileId)
        await validateUploadFilesView(this)
      })
    },
    async setThemedCollectionSelection({ commit }: ActionContext<State, State>, title: string) {
      commit('setThemedCollectionResponse', title)
      await validateUploadFilesView(this)
    },
    async setTitleAndAbstract({ commit }: ActionContext<State, State>, payload: TitleAbstract) {
      commit('_setTitle', payload.title)
      commit('_setAbstract', payload.abstract)
    },
    async setCcdcNumbers({ commit }: ActionContext<State, State>, numbers: string) {
      commit('_setCcdcNumbers', numbers)
    },
    async addEditorialOffice({ commit }: ActionContext<State, State>, editor: EditorialOffice) {
      commit('_addEditorialOffice', editor)
      await validateReviewEditorialView(this)
    },
    async setEditorialOfficeSelectionMode({ commit }: ActionContext<State, State>, editorialOfficeSelectionMode: EditorialOfficeSelectionMode) {
      commit('_setEditorialOfficeSelectionMode', editorialOfficeSelectionMode)
      await validateReviewEditorialView(this)
    },
    async setReviewerAnonymisation({ commit }: ActionContext<State, State>, peerReviewAnonymisation: ReviewerAnonymisationType) {
      commit('_setReviewerAnonymisation', peerReviewAnonymisation)
      await validateUploadFilesView(this)
    },
    async setTPRType({ commit }: ActionContext<State, State>, transparentPeerReviewType: TransparentPeerReviewType) {
      commit('_setTPRType', transparentPeerReviewType)
      await validateReviewEditorialView(this)
    },
    async upsertReviewer({ commit }: ActionContext<State, State>, reviewer: Reviewer) {
      commit('_upsertReviewer', reviewer)
      await validateReviewEditorialView(this)
    },
    async removeReviewer({ commit }: ActionContext<State, State>, reviewer: Reviewer) {
      commit('_removeReviewer', reviewer)
      await validateReviewEditorialView(this)
    },
    async updateTermsAgreed({ commit }: ActionContext<State, State>, agree: boolean) {
      commit('_updateTermsAgreed', agree)
      await validateReviewStep(this)
    },
    async completeSubmission({ commit, state }: ActionContext<State, State>) {
      if (state.completed) {
        throw new Error('This submission has already been completed, it will not be submitted again')
      }

      return await submissionApi.finaliseSubmission(state.activeSubmission).then(() => {
        commit('_markAsComplete')
      })
    },
    async removeEditorialOffice({ commit }: ActionContext<State, State>, editorId: string) {
      commit('_deleteEditorialOffice', editorId)
      await validateReviewEditorialView(this)
    },
    setSelectedEditorialOffices({ commit }, editors: EditorialOffice[]) {
      commit('_setSelectedEditorialOffices', editors)
    },
    async fetchEditorialOffices({ commit }: ActionContext<State, State>) {
      const resp = await submissionApi.getEditorialOfficeIds()
      const offices = Array.isArray(resp.data) ? resp.data : []
      commit('setEditorialOffices', offices)
    },

  },
  getters: {
    submissionFiles: (state: State) => {
      return state.activeSubmission.files.sort((a, b) => submissionFileTypeToNumber(a.type) - submissionFileTypeToNumber(b.type))
    },
    groupedSubmissionFiles: (state: State) => {
      return Object.entries(
        state.activeSubmission.files.reduce((acc, file) => {
          ;(acc[file.type] ||= []).push(file)
          return acc
        }, {} as Record<string, SubmissionFile[]>),
      )
        .map(([type, files]) => ({ type, files }))
        .sort((a, b) => submissionFileTypeToNumber(a.type as SubmissionFileType) - submissionFileTypeToNumber(b.type as SubmissionFileType))
    },
    primaryAuthor: (state: State) => state.activeSubmission.authors.find(author => author.isCorresponding),
    getVerifyResultByName: (state: State) => (name: string) => {
      const entry = (state.errors as Record<string, VerifyResult>)[name]
      return entry ?? null
    },
    hasIngestedData: (state: State) => !!state.dataToCompare?.ingestedAt,
    isFeatureFlagEnabled: (state: State) => (flag: string): boolean =>
      state.featureFlagsEnabled.includes(flag),
  },
  plugins: [new VuexPersistence({
    key: 'rsc_submission',
    storage: window.localStorage,
    reducer: (state) => {
      const { ...others } = state
      const item = { data: others, expiry: expirationDate }
      return item
    },
    restoreState: restoreAndEnsureStateSanitised,
  }).plugin],
})

type EventListener = (event: Event) => void

function bindEvent(element: Window, eventName: string, eventHandler: (event: MessageEvent) => void) {
  if (element.addEventListener) {
    element.addEventListener(eventName, eventHandler as EventListener, false)
  }
}

export function restoreAndEnsureStateSanitised(key: string, storage?: Storage): State {
  let state = {} as State
  try {
    const item = storage?.getItem(key)
    const localStorageData: LocalStorage = item ? JSON.parse(item) : null
    state = localStorageData.data as State
    expirationDate = localStorageData.expiry
  }
  catch {
    return structuredClone(initialState)
  }
  ensureAuthorsSanitised(state.activeSubmission)
  if (state.dataToCompare?.authors?.length) {
    ensureAuthorsSanitised({
      authors: state.dataToCompare.authors,
    } as unknown as Submission)
  }
  return state
}
